import { useQuery } from '@apollo/client';
import React, { useContext, useMemo, useState, createContext } from 'react';

import {
  GetCapitalProvidersQueryGQLType,
  GetCapitalProvidersQueryVariablesGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { GET_CAPITAL_PROVIDERS_FOR_CREDIT_FACILITY } from 'packages/organization/requests/organization.gql';
import CapitalProviderMissing from 'packages/organization/ui/CapitalProviderMissing';

type CapitalProviderContextType = {
  organizationId: string;
  setOrganizationId: (nextId: string) => void;
};
export const CapitalProviderContext = createContext<CapitalProviderContextType | null>(
  null,
);

export const useCapitalProviderContext = () => {
  const context = useContext(CapitalProviderContext);
  if (context === null) {
    throw new Error('CapitalProviderContext must be present');
  }

  return context;
};

type CapitalProviderContextProviderProps = {
  children: React.ReactNode;
  renderLoading: React.ReactElement;
};
const CapitalProviderContextProvider = ({
  renderLoading,
  children,
}: CapitalProviderContextProviderProps) => {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { creditFacilityId } = useCreditFacilityContext();

  const {
    data: capitalProvidersData,
    loading: capitalProvidersLoading,
  } = useQuery<
    GetCapitalProvidersQueryGQLType,
    GetCapitalProvidersQueryVariablesGQLType
  >(GET_CAPITAL_PROVIDERS_FOR_CREDIT_FACILITY, {
    variables: { creditFacilityId },
  });

  const context = useMemo(() => {
    if (organizationId) {
      return { organizationId, setOrganizationId };
    }

    if (
      capitalProvidersData &&
      capitalProvidersData.capitalProviders &&
      capitalProvidersData.capitalProviders.length > 0
    ) {
      return {
        organizationId: capitalProvidersData.capitalProviders[0].id,
        setOrganizationId,
      };
    }

    return null;
  }, [organizationId, setOrganizationId, capitalProvidersData]);

  if (capitalProvidersLoading || !context) {
    return renderLoading;
  }

  if (
    !capitalProvidersLoading &&
    capitalProvidersData &&
    capitalProvidersData.capitalProviders &&
    capitalProvidersData.capitalProviders.length === 0
  ) {
    return <CapitalProviderMissing />;
  }

  return (
    <CapitalProviderContext.Provider value={context}>
      {children}
    </CapitalProviderContext.Provider>
  );
};

export default CapitalProviderContextProvider;
